import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  DollarSign, ArrowLeft, Search, Check, FileText, CreditCard, Download, Plus, Edit, Trash2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
  { value: '1', label: 'Janvier' },
  { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' },
  { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' },
  { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' }
];

export const AdminCommissions = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [apporteurs, setApporteurs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('commissions');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApporteur, setFilterApporteur] = useState('all');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'virement',
    reference: '',
    notes: ''
  });

  // New commission form state
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [editingCommissionId, setEditingCommissionId] = useState(null);
  const [commissionFormData, setCommissionFormData] = useState({
    contract_id: '',
    apporteur_id: '',
    amount: 0,
    commission_type: 'recurrente',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date().toISOString().split('T')[0],
    payment_date: '',
    status: 'en_attente',
    notes: ''
  });

  // Monthly statement form state
  const [showMonthlyStatementForm, setShowMonthlyStatementForm] = useState(false);
  const [monthlyStatementData, setMonthlyStatementData] = useState({
    apporteur_id: '',
    month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear())
  });

  // Period action menu state (for + button in tracking)
  const [periodActionMenu, setPeriodActionMenu] = useState(null); // { contractId, month, year, x, y }
  const [naperiods, setNaperiods] = useState([]); // List of N/A periods

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [commissionsRes, contractsRes, apporteursRes, paymentsRes, naperiodsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/commissions`),
        axios.get(`${API_URL}/api/admin/contracts`),
        axios.get(`${API_URL}/api/admin/apporteurs`),
        axios.get(`${API_URL}/api/admin/payments`),
        axios.get(`${API_URL}/api/admin/naperiods`).catch(() => ({ data: [] }))
      ]);
      setCommissions(commissionsRes.data);
      setContracts(contractsRes.data);
      setApporteurs(apporteursRes.data);
      setPayments(paymentsRes.data);
      setNaperiods(naperiodsRes.data || []);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Reset commission form
  const resetCommissionForm = () => {
    setCommissionFormData({
      contract_id: '',
      apporteur_id: '',
      amount: 0,
      commission_type: 'recurrente',
      period_start: new Date().toISOString().split('T')[0],
      period_end: new Date().toISOString().split('T')[0],
      payment_date: '',
      status: 'en_attente',
      notes: ''
    });
    setEditingCommissionId(null);
  };

  // Handle contract selection - auto-fill apporteur and suggested amount
  // La commission courtier saisie correspond déjà au montant par échéance (selon le fractionnement)
  const handleContractSelect = (contractId) => {
    const contract = contracts.find(c => c.id === contractId);
    if (contract) {
      setCommissionFormData(prev => ({
        ...prev,
        contract_id: contractId,
        apporteur_id: contract.apporteur_id,
        amount: contract.apporteur_commission || 0 // Commission déjà au bon montant par échéance
      }));
    }
  };

  // Get selected contract info for display
  const getSelectedContractInfo = () => {
    if (!commissionFormData.contract_id) return null;
    return contracts.find(c => c.id === commissionFormData.contract_id);
  };

  // Submit commission form (create or update)
  const handleCommissionSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        contract_id: commissionFormData.contract_id,
        apporteur_id: commissionFormData.apporteur_id,
        amount: parseFloat(commissionFormData.amount),
        commission_type: commissionFormData.commission_type,
        period_start: new Date(commissionFormData.period_start).toISOString(),
        period_end: new Date(commissionFormData.period_end).toISOString(),
        payment_date: commissionFormData.payment_date ? new Date(commissionFormData.payment_date).toISOString() : null,
        status: commissionFormData.status,
        notes: commissionFormData.notes
      };

      if (editingCommissionId) {
        // Update existing commission
        const { contract_id, apporteur_id, ...updateData } = data;
        await axios.put(`${API_URL}/api/admin/commissions/${editingCommissionId}`, updateData);
        toast.success('Commission modifiée');
      } else {
        // Create new commission
        await axios.post(`${API_URL}/api/admin/commissions`, data);
        toast.success('Commission créée');
      }
      setShowCommissionForm(false);
      resetCommissionForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Edit commission
  const handleEditCommission = (commission) => {
    setCommissionFormData({
      contract_id: commission.contract_id,
      apporteur_id: commission.apporteur_id,
      amount: commission.amount,
      commission_type: commission.commission_type || 'recurrente',
      period_start: commission.period_start?.split('T')[0] || '',
      period_end: commission.period_end?.split('T')[0] || '',
      payment_date: commission.payment_date?.split('T')[0] || '',
      status: commission.status,
      notes: commission.notes || ''
    });
    setEditingCommissionId(commission.id);
    setShowCommissionForm(true);
  };

  // Download monthly statement PDF
  const handleDownloadMonthlyStatement = async () => {
    if (!monthlyStatementData.apporteur_id) {
      toast.error('Veuillez sélectionner un apporteur');
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/releve-mensuel/${monthlyStatementData.apporteur_id}/pdf?month=${monthlyStatementData.month}&year=${monthlyStatementData.year}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const apporteur = apporteurs.find(a => a.id === monthlyStatementData.apporteur_id);
      const monthName = MONTHS.find(m => m.value === monthlyStatementData.month)?.label || '';
      link.setAttribute('download', `releve_mensuel_${apporteur?.name || 'apporteur'}_${monthName}_${monthlyStatementData.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relevé mensuel téléchargé');
      setShowMonthlyStatementForm(false);
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const generateCommissions = async () => {
    // Generate commissions from active contracts that don't have a commission for this period
    const now = new Date();
    const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const activeContracts = contracts.filter(c => c.status === 'actif');
    let created = 0;

    for (const contract of activeContracts) {
      // Check if commission already exists for this period
      const exists = commissions.some(c => 
        c.contract_id === contract.id &&
        new Date(c.period_start).getMonth() === periodStart.getMonth() &&
        new Date(c.period_start).getFullYear() === periodStart.getFullYear()
      );

      if (!exists) {
        try {
          await axios.post(`${API_URL}/api/admin/commissions`, {
            contract_id: contract.id,
            apporteur_id: contract.apporteur_id,
            amount: contract.apporteur_commission,
            period_start: periodStart.toISOString(),
            period_end: periodEnd.toISOString(),
            status: 'en_attente'
          });
          created++;
        } catch (err) {
          console.error('Error creating commission', err);
        }
      }
    }

    if (created > 0) {
      toast.success(`${created} commission(s) générée(s)`);
      fetchData();
    } else {
      toast.info('Aucune nouvelle commission à générer');
    }
  };

  const handleExportPDF = async (apporteurId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/bordereau/${apporteurId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur export');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bordereau_${apporteurId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (err) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const handleValidateCommission = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/commissions/${id}`, { status: 'valide' });
      toast.success('Commission validée');
      fetchData();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Delete commission (even paid ones - for error correction)
  const handleDeleteCommission = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commission ? Cette action est irréversible.')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/admin/commissions/${id}`);
      toast.success('Commission supprimée');
      // Remove from selected if it was selected
      setSelectedCommissions(prev => prev.filter(c => c.id !== id));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Delete payment from history
  const handleDeletePayment = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ? Les commissions associées seront remises au statut "validé".')) {
      return;
    }
    try {
      const response = await axios.delete(`${API_URL}/api/admin/payments/${id}`);
      toast.success(`Paiement supprimé. ${response.data.commissions_reset} commission(s) remise(s) au statut "validé".`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Handle period action menu (+ button click)
  const handlePeriodActionClick = (e, contractId, month, year) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPeriodActionMenu({
      contractId,
      month,
      year,
      x: rect.left,
      y: rect.bottom + 5
    });
  };

  // Close period action menu
  const closePeriodActionMenu = () => {
    setPeriodActionMenu(null);
  };

  // Create commission from period action menu
  const handleCreateCommissionFromMenu = () => {
    if (!periodActionMenu) return;
    const { contractId, month, year } = periodActionMenu;
    const contract = contracts.find(c => c.id === contractId);
    if (contract) {
      const periodStart = new Date(year, month - 1, 1);
      const periodEnd = new Date(year, month, 0);
      handleContractSelect(contractId);
      setCommissionFormData(prev => ({
        ...prev,
        contract_id: contractId,
        apporteur_id: contract.apporteur_id,
        amount: contract.apporteur_commission || 0,
        period_start: periodStart.toISOString().split('T')[0],
        period_end: periodEnd.toISOString().split('T')[0]
      }));
      setShowCommissionForm(true);
    }
    closePeriodActionMenu();
  };

  // Mark period as N/A
  const handleMarkPeriodNA = async () => {
    if (!periodActionMenu) return;
    const { contractId, month, year } = periodActionMenu;
    try {
      await axios.post(`${API_URL}/api/admin/naperiods`, {
        contract_id: contractId,
        month: month,
        year: year,
        reason: 'Marqué N/A manuellement'
      });
      toast.success('Période marquée comme N/A');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    closePeriodActionMenu();
  };

  // Remove N/A period
  const handleRemoveNAPeriod = async (contractId, month, year) => {
    try {
      await axios.delete(`${API_URL}/api/admin/naperiods/${contractId}/${year}/${month}`);
      toast.success('Période N/A supprimée');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Check if a period is marked as N/A
  const isPeriodNA = (contractId, month, year) => {
    return naperiods.some(p => 
      p.contract_id === contractId && 
      p.month === month && 
      p.year === year
    );
  };

  const handleSelectCommission = (commission) => {
    if (commission.status === 'paye') return;
    
    setSelectedCommissions(prev => {
      const exists = prev.find(c => c.id === commission.id);
      if (exists) {
        return prev.filter(c => c.id !== commission.id);
      } else {
        // Only allow selecting commissions from same apporteur
        if (prev.length > 0 && prev[0].apporteur_id !== commission.apporteur_id) {
          toast.error('Sélectionnez uniquement des commissions du même apporteur');
          return prev;
        }
        return [...prev, commission];
      }
    });
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (selectedCommissions.length === 0) {
      toast.error('Sélectionnez des commissions');
      return;
    }

    const totalAmount = selectedCommissions.reduce((sum, c) => sum + c.amount, 0);
    const apporteurId = selectedCommissions[0].apporteur_id;

    try {
      await axios.post(`${API_URL}/api/admin/payments`, {
        apporteur_id: apporteurId,
        total_amount: totalAmount,
        commission_ids: selectedCommissions.map(c => c.id),
        payment_date: new Date(paymentData.payment_date).toISOString(),
        payment_method: paymentData.payment_method,
        reference: paymentData.reference,
        notes: paymentData.notes
      });
      toast.success('Paiement enregistré');
      setShowPaymentForm(false);
      setSelectedCommissions([]);
      setPaymentData({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'virement',
        reference: '',
        notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const filteredCommissions = commissions.filter(c => {
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesApporteur = filterApporteur === 'all' || c.apporteur_id === filterApporteur;
    return matchesStatus && matchesApporteur;
  });

  const pendingCommissions = commissions.filter(c => c.status !== 'paye');
  const totalPending = pendingCommissions.reduce((sum, c) => sum + c.amount, 0);
  const selectedTotal = selectedCommissions.reduce((sum, c) => sum + c.amount, 0);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Commissions & Paiements | Admin CAMBC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-100 pt-20 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-900">Commissions & Paiements</h1>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => { resetCommissionForm(); setShowCommissionForm(true); }}>
                <Plus size={18} className="mr-2" /> Nouvelle Commission
              </Button>
              <Button variant="outline" onClick={() => setShowMonthlyStatementForm(true)}>
                <Calendar size={18} className="mr-2" /> Relevé Mensuel
              </Button>
              {selectedCommissions.length > 0 && (
                <Button onClick={() => setShowPaymentForm(true)} className="bg-green-600 hover:bg-green-700">
                  <CreditCard size={18} className="mr-2" /> 
                  Payer ({selectedTotal.toLocaleString('fr-FR')} €)
                </Button>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Commissions en attente</p>
                  <p className="text-2xl font-bold text-orange-700">{totalPending.toLocaleString('fr-FR')} €</p>
                </div>
                <DollarSign className="text-orange-400" size={32} />
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Sélectionnées</p>
                  <p className="text-2xl font-bold text-blue-700">{selectedTotal.toLocaleString('fr-FR')} €</p>
                </div>
                <Check className="text-blue-400" size={32} />
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Paiements effectués</p>
                  <p className="text-2xl font-bold text-green-700">{payments.length}</p>
                </div>
                <CreditCard className="text-green-400" size={32} />
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button 
              variant={activeTab === 'commissions' ? 'default' : 'outline'}
              onClick={() => setActiveTab('commissions')}
            >
              Commissions
            </Button>
            <Button 
              variant={activeTab === 'suivi' ? 'default' : 'outline'}
              onClick={() => setActiveTab('suivi')}
              className={activeTab === 'suivi' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              📊 Suivi par Contrat
            </Button>
            <Button 
              variant={activeTab === 'payments' ? 'default' : 'outline'}
              onClick={() => setActiveTab('payments')}
            >
              Historique Paiements
            </Button>
          </div>

          {/* Payment Form Modal */}
          {showPaymentForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle>Enregistrer le paiement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-blue-600">Apporteur: <strong>{selectedCommissions[0]?.apporteur_name}</strong></p>
                    <p className="text-sm text-blue-600">Commissions: <strong>{selectedCommissions.length}</strong></p>
                    <p className="text-xl font-bold text-blue-700 mt-2">Total: {selectedTotal.toLocaleString('fr-FR')} €</p>
                  </div>
                  <form onSubmit={handleCreatePayment} className="space-y-4">
                    <div>
                      <Label>Date du paiement</Label>
                      <Input
                        type="date"
                        value={paymentData.payment_date}
                        onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Mode de paiement</Label>
                      <Select 
                        value={paymentData.payment_method} 
                        onValueChange={(v) => setPaymentData({...paymentData, payment_method: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="virement">Virement bancaire</SelectItem>
                          <SelectItem value="cheque">Chèque</SelectItem>
                          <SelectItem value="especes">Espèces</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Référence (optionnel)</Label>
                      <Input
                        value={paymentData.reference}
                        onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                        placeholder="N° de virement, chèque..."
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1">
                        Confirmer le paiement
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowPaymentForm(false)}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Commission Form Modal */}
          {showCommissionForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <Card className="w-full max-w-2xl my-8">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-t-lg">
                  <CardTitle>{editingCommissionId ? 'Modifier Commission' : 'Nouvelle Commission'}</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleCommissionSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contrat *</Label>
                        <Select 
                          value={commissionFormData.contract_id} 
                          onValueChange={handleContractSelect}
                          disabled={!!editingCommissionId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un contrat" />
                          </SelectTrigger>
                          <SelectContent>
                            {contracts.filter(c => c.status === 'actif').map(c => {
                              const freqLabels = { unique: 'Unique', mensuel: 'Mens.', trimestriel: 'Trim.', semestriel: 'Sem.', annuel: 'Ann.' };
                              const companyName = c.insurance_company || 'N/A';
                              const contractNum = c.contract_number ? `N°${c.contract_number}` : '';
                              return (
                                <SelectItem key={c.id} value={c.id}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{c.client_name} - {companyName}</span>
                                    <span className="text-xs text-slate-500">
                                      {c.insurance_type} {contractNum && `• ${contractNum}`} • {freqLabels[c.payment_frequency] || 'Ann.'}
                                    </span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Apporteur</Label>
                        <Input
                          value={apporteurs.find(a => a.id === commissionFormData.apporteur_id)?.name || ''}
                          disabled
                          className="bg-slate-100"
                        />
                      </div>
                    </div>

                    {/* Contract info box */}
                    {getSelectedContractInfo() && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-blue-200">
                          <span className="text-blue-800 font-bold">{getSelectedContractInfo()?.insurance_company || 'Compagnie N/A'}</span>
                          {getSelectedContractInfo()?.contract_number && (
                            <span className="text-blue-600 text-sm">N° {getSelectedContractInfo()?.contract_number}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-blue-600 font-medium">Fractionnement:</span>
                            <p className="text-blue-800 font-bold">
                              {{ unique: 'Unique', mensuel: 'Mensuel', trimestriel: 'Trimestriel', semestriel: 'Semestriel', annuel: 'Annuel' }[getSelectedContractInfo()?.payment_frequency] || 'Annuel'}
                            </p>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Commission courtier:</span>
                            <p className="text-blue-800 font-bold">{getSelectedContractInfo()?.broker_commission?.toLocaleString('fr-FR')} €</p>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Commission apporteur:</span>
                            <p className="text-blue-800 font-bold">{getSelectedContractInfo()?.apporteur_commission?.toLocaleString('fr-FR')} €</p>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Prime client:</span>
                            <p className="text-blue-800 font-bold">{getSelectedContractInfo()?.premium_amount?.toLocaleString('fr-FR')} €</p>
                          </div>
                        </div>
                        <p className="text-xs text-blue-500 mt-2">Les montants correspondent à une échéance selon le fractionnement choisi</p>
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>Montant commission (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={commissionFormData.amount}
                          onChange={(e) => setCommissionFormData({...commissionFormData, amount: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Type de commission *</Label>
                        <Select 
                          value={commissionFormData.commission_type} 
                          onValueChange={(v) => setCommissionFormData({...commissionFormData, commission_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recurrente">Récurrente</SelectItem>
                            <SelectItem value="unique">Unique (frais dossier...)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Début période *</Label>
                        <Input
                          type="date"
                          value={commissionFormData.period_start}
                          onChange={(e) => setCommissionFormData({...commissionFormData, period_start: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Fin période *</Label>
                        <Input
                          type="date"
                          value={commissionFormData.period_end}
                          onChange={(e) => setCommissionFormData({...commissionFormData, period_end: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Date de reversement</Label>
                        <Input
                          type="date"
                          value={commissionFormData.payment_date}
                          onChange={(e) => setCommissionFormData({...commissionFormData, payment_date: e.target.value})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Date versement à l'apporteur</p>
                      </div>
                      <div>
                        <Label>Statut</Label>
                        <Select 
                          value={commissionFormData.status} 
                          onValueChange={(v) => setCommissionFormData({...commissionFormData, status: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="valide">Validé</SelectItem>
                            <SelectItem value="paye">Payé</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={commissionFormData.notes}
                        onChange={(e) => setCommissionFormData({...commissionFormData, notes: e.target.value})}
                        rows={2}
                        placeholder="Informations complémentaires..."
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1">
                        {editingCommissionId ? 'Modifier' : 'Créer la commission'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => { setShowCommissionForm(false); resetCommissionForm(); }}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Monthly Statement Form Modal */}
          {showMonthlyStatementForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-800 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar size={20} /> Relevé Mensuel des Versements
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-sm text-slate-600 mb-4">
                    Générez un PDF récapitulant tous les versements effectués à un apporteur pour un mois donné.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label>Apporteur *</Label>
                      <Select 
                        value={monthlyStatementData.apporteur_id} 
                        onValueChange={(v) => setMonthlyStatementData({...monthlyStatementData, apporteur_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un apporteur" />
                        </SelectTrigger>
                        <SelectContent>
                          {apporteurs.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Mois</Label>
                        <Select 
                          value={monthlyStatementData.month} 
                          onValueChange={(v) => setMonthlyStatementData({...monthlyStatementData, month: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MONTHS.map(m => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Année</Label>
                        <Select 
                          value={monthlyStatementData.year} 
                          onValueChange={(v) => setMonthlyStatementData({...monthlyStatementData, year: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map(y => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button onClick={handleDownloadMonthlyStatement} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        <Download size={18} className="mr-2" /> Télécharger PDF
                      </Button>
                      <Button variant="outline" onClick={() => setShowMonthlyStatementForm(false)}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'commissions' && (
            <>
              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="en_attente">En attente</SelectItem>
                    <SelectItem value="valide">Validé</SelectItem>
                    <SelectItem value="paye">Payé</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterApporteur} onValueChange={setFilterApporteur}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrer par apporteur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les apporteurs</SelectItem>
                    {apporteurs.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filterApporteur !== 'all' && (
                  <Button 
                    variant="outline" 
                    onClick={() => handleExportPDF(filterApporteur)}
                    className="ml-auto"
                  >
                    <Download size={16} className="mr-2" /> Exporter PDF
                  </Button>
                )}
              </div>

              {/* Commissions Table */}
              <Card className="bg-white shadow-lg border-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="p-4 w-12">
                          <span className="sr-only">Sélection</span>
                        </th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Apporteur</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Contrat</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Compagnie</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Type</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Mois Prime</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Date Revers.</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Montant</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Statut</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredCommissions.length > 0 ? (
                        filteredCommissions.map((commission) => (
                          <tr 
                            key={commission.id} 
                            className={`hover:bg-slate-50 cursor-pointer ${
                              selectedCommissions.find(c => c.id === commission.id) ? 'bg-blue-50' : ''
                            } ${commission.status === 'paye' ? 'opacity-60' : ''}`}
                            onClick={() => handleSelectCommission(commission)}
                          >
                            <td className="p-4 text-center">
                              <input
                                type="checkbox"
                                checked={!!selectedCommissions.find(c => c.id === commission.id)}
                                onChange={() => {}}
                                disabled={commission.status === 'paye'}
                                className="rounded border-slate-300"
                              />
                            </td>
                            <td className="p-4 font-medium text-slate-900">{commission.apporteur_name}</td>
                            <td className="p-4">
                              <p className="text-slate-900">{commission.contract_info?.client_name}</p>
                              <p className="text-sm text-slate-500">{commission.contract_info?.insurance_type}</p>
                            </td>
                            <td className="p-4 text-slate-600 text-sm">
                              {commission.contract_info?.insurance_company || '-'}
                            </td>
                            <td className="p-4 text-slate-600 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${
                                commission.commission_type === 'unique' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {commission.commission_type === 'unique' ? 'Unique' : 'Récur.'}
                              </span>
                            </td>
                            <td className="p-4 text-slate-600 text-sm">
                              {commission.prime_month ? (() => {
                                const parts = commission.prime_month.split('-');
                                return parts.length === 2 ? `${parts[1]}/${parts[0]}` : commission.prime_month;
                              })() : '-'}
                            </td>
                            <td className="p-4 text-slate-600 text-sm">
                              {commission.payment_date ? new Date(commission.payment_date).toLocaleDateString('fr-FR') : '-'}
                            </td>
                            <td className="p-4 font-medium text-blue-600">{commission.amount?.toLocaleString('fr-FR')} €</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                commission.status === 'paye' ? 'bg-green-100 text-green-700' :
                                commission.status === 'valide' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {commission.status === 'paye' ? 'Payé' : 
                                 commission.status === 'valide' ? 'Validé' : 'En attente'}
                              </span>
                            </td>
                            <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" onClick={() => handleEditCommission(commission)}>
                                <Edit size={16} />
                              </Button>
                              {commission.status === 'en_attente' && (
                                <Button variant="ghost" size="sm" onClick={() => handleValidateCommission(commission.id)}>
                                  <Check size={16} className="text-green-600" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteCommission(commission.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="p-8 text-center text-slate-500">
                            <DollarSign size={48} className="mx-auto mb-4 text-slate-300" />
                            <p>Aucune commission trouvée</p>
                            <Button 
                              onClick={() => { resetCommissionForm(); setShowCommissionForm(true); }}
                              className="mt-4"
                            >
                              <Plus size={16} className="mr-2" /> Créer une commission
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* Contract Commission Tracking Dashboard */}
          {activeTab === 'suivi' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-emerald-100 text-sm">Contrats actifs</p>
                    <p className="text-3xl font-bold">{contracts.filter(c => c.status === 'actif').length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-blue-100 text-sm">Commissions versées</p>
                    <p className="text-3xl font-bold">{commissions.filter(c => c.status === 'paye').length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-orange-100 text-sm">En attente</p>
                    <p className="text-3xl font-bold">{commissions.filter(c => c.status !== 'paye').length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-red-100 text-sm">Mois sans commission</p>
                    <p className="text-3xl font-bold">
                      {(() => {
                        const currentYear = new Date().getFullYear();
                        const currentMonth = new Date().getMonth() + 1;
                        let missing = 0;
                        contracts.filter(c => c.status === 'actif' && c.payment_frequency !== 'unique').forEach(contract => {
                          // Skip contracts with N/A effect date (old system)
                          if (contract.effect_date_na === true) return;
                          
                          // Get contract effect date
                          const effectDate = contract.effect_date ? new Date(contract.effect_date) : null;
                          const effectMonth = effectDate ? effectDate.getMonth() + 1 : null;
                          const effectYear = effectDate ? effectDate.getFullYear() : null;
                          
                          // Check last 6 months for missing commissions
                          for (let i = 0; i < 6; i++) {
                            let checkMonth = currentMonth - i;
                            let checkYear = currentYear;
                            if (checkMonth <= 0) {
                              checkMonth += 12;
                              checkYear -= 1;
                            }
                            
                            // Skip months before contract effect date
                            if (effectDate && (checkYear < effectYear || (checkYear === effectYear && checkMonth < effectMonth))) {
                              continue;
                            }
                            
                            // Check if commission exists for this period based on period_start
                            const hasCommission = commissions.some(c => {
                              if (c.contract_id !== contract.id) return false;
                              if (!c.period_start) return false;
                              const periodDate = new Date(c.period_start);
                              return periodDate.getMonth() + 1 === checkMonth && periodDate.getFullYear() === checkYear;
                            });
                            if (!hasCommission) missing++;
                          }
                        });
                        return missing;
                      })()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Contract Tracking Table */}
              <Card className="bg-white shadow-lg border-0 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-800 text-white">
                  <CardTitle className="flex items-center gap-2">
                    📋 Suivi des Rétrocessions par Contrat
                  </CardTitle>
                  <p className="text-emerald-100 text-sm mt-1">
                    Vue d'ensemble des commissions versées et manquantes pour chaque contrat actif (6 derniers mois)
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-4 text-sm font-semibold text-slate-600 sticky left-0 bg-slate-50">Contrat</th>
                          <th className="text-left p-4 text-sm font-semibold text-slate-600">Compagnie</th>
                          <th className="text-left p-4 text-sm font-semibold text-slate-600">Apporteur</th>
                          <th className="text-left p-4 text-sm font-semibold text-slate-600">Fréquence</th>
                          <th className="text-left p-4 text-sm font-semibold text-slate-600">Date Effet</th>
                          {/* Generate last 6 months headers */}
                          {(() => {
                            const headers = [];
                            const currentYear = new Date().getFullYear();
                            const currentMonth = new Date().getMonth() + 1;
                            for (let i = 5; i >= 0; i--) {
                              let m = currentMonth - i;
                              let y = currentYear;
                              if (m <= 0) { m += 12; y -= 1; }
                              const monthLabel = MONTHS.find(mon => mon.value === String(m))?.label?.substring(0, 3) || m;
                              headers.push(
                                <th key={`${y}-${m}`} className="text-center p-2 text-xs font-semibold text-slate-600 min-w-[70px]">
                                  {monthLabel}<br/><span className="text-slate-400">{y}</span>
                                </th>
                              );
                            }
                            return headers;
                          })()}
                          <th className="text-center p-4 text-sm font-semibold text-slate-600">Total Versé</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {contracts.filter(c => c.status === 'actif').length > 0 ? (
                          contracts.filter(c => c.status === 'actif').map((contract) => {
                            const apporteur = apporteurs.find(a => a.id === contract.apporteur_id);
                            const contractCommissions = commissions.filter(c => c.contract_id === contract.id);
                            const totalPaid = contractCommissions.filter(c => c.status === 'paye').reduce((sum, c) => sum + c.amount, 0);
                            const freqLabels = { unique: 'Unique', mensuel: 'Mensuel', trimestriel: 'Trim.', semestriel: 'Sem.', annuel: 'Annuel' };
                            
                            // Get contract effect date - handle N/A case
                            const isEffectDateNA = contract.effect_date_na === true;
                            const effectDate = !isEffectDateNA && contract.effect_date ? new Date(contract.effect_date) : null;
                            const effectMonth = effectDate ? effectDate.getMonth() + 1 : null;
                            const effectYear = effectDate ? effectDate.getFullYear() : null;
                            
                            return (
                              <tr key={contract.id} className="hover:bg-slate-50">
                                <td className="p-4 sticky left-0 bg-white">
                                  <p className="font-medium text-slate-900">{contract.client_name}</p>
                                  <p className="text-xs text-slate-500">{contract.insurance_type}</p>
                                  {contract.contract_number && (
                                    <p className="text-xs text-slate-400">N°{contract.contract_number}</p>
                                  )}
                                </td>
                                <td className="p-4 text-sm text-slate-600">{contract.insurance_company || '-'}</td>
                                <td className="p-4 text-sm text-slate-600">{apporteur?.name || '-'}</td>
                                <td className="p-4">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    contract.payment_frequency === 'unique' ? 'bg-purple-100 text-purple-700' :
                                    contract.payment_frequency === 'mensuel' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-600'
                                  }`}>
                                    {freqLabels[contract.payment_frequency] || 'Ann.'}
                                  </span>
                                </td>
                                <td className="p-4 text-sm">
                                  {isEffectDateNA ? (
                                    <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-500">N/A</span>
                                  ) : effectDate ? (
                                    <span className="text-slate-600">{effectDate.toLocaleDateString('fr-FR')}</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                {/* Generate cells for last 6 months */}
                                {(() => {
                                  const cells = [];
                                  const currentYear = new Date().getFullYear();
                                  const currentMonth = new Date().getMonth() + 1;
                                  for (let i = 5; i >= 0; i--) {
                                    let m = currentMonth - i;
                                    let y = currentYear;
                                    if (m <= 0) { m += 12; y -= 1; }
                                    const monthKey = `${y}-${String(m).padStart(2, '0')}`;
                                    
                                    // Check if this month is before the contract effect date
                                    const isBeforeEffectDate = effectDate && (
                                      y < effectYear || 
                                      (y === effectYear && m < effectMonth)
                                    );
                                    
                                    // Find commission by period_start month/year
                                    const commission = contractCommissions.find(c => {
                                      if (!c.period_start) return false;
                                      const periodDate = new Date(c.period_start);
                                      return periodDate.getMonth() + 1 === m && periodDate.getFullYear() === y;
                                    });
                                    
                                    // For unique frequency, only show first month or N/A
                                    if (contract.payment_frequency === 'unique') {
                                      if (i === 5) {
                                        const anyCommission = contractCommissions.find(c => c.status === 'paye');
                                        cells.push(
                                          <td key={monthKey} className="p-2 text-center" colSpan={1}>
                                            {anyCommission ? (
                                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600">
                                                <Check size={16} />
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-xs font-bold">
                                                ?
                                              </span>
                                            )}
                                          </td>
                                        );
                                      } else {
                                        cells.push(
                                          <td key={monthKey} className="p-2 text-center">
                                            <span className="text-slate-300">-</span>
                                          </td>
                                        );
                                      }
                                    } else if (isBeforeEffectDate) {
                                      // Month is before contract effect date - show N/A
                                      cells.push(
                                        <td key={monthKey} className="p-2 text-center">
                                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-400 text-xs" title="Avant date d'effet">
                                            N/A
                                          </span>
                                        </td>
                                      );
                                    } else if (isPeriodNA(contract.id, m, y)) {
                                      // Period is manually marked as N/A
                                      cells.push(
                                        <td key={monthKey} className="p-2 text-center">
                                          <button
                                            onClick={() => handleRemoveNAPeriod(contract.id, m, y)}
                                            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors text-xs font-bold"
                                            title="Période marquée N/A - Cliquer pour supprimer"
                                          >
                                            N/A
                                          </button>
                                        </td>
                                      );
                                    } else {
                                      cells.push(
                                        <td key={monthKey} className="p-2 text-center">
                                          {commission ? (
                                            commission.status === 'paye' ? (
                                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600" title={`${commission.amount}€ - Payé`}>
                                                <Check size={16} />
                                              </span>
                                            ) : commission.status === 'valide' ? (
                                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-xs font-bold" title={`${commission.amount}€ - Validé`}>
                                                ✓
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-xs font-bold" title={`${commission.amount}€ - En attente`}>
                                                ⏳
                                              </span>
                                            )
                                          ) : (
                                            <button
                                              onClick={(e) => handlePeriodActionClick(e, contract.id, m, y)}
                                              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                              title="Commission manquante - Cliquer pour choisir une action"
                                            >
                                              <Plus size={16} />
                                            </button>
                                          )}
                                        </td>
                                      );
                                    }
                                  }
                                  return cells;
                                })()}
                                <td className="p-4 text-center">
                                  <span className="font-bold text-green-600">{totalPaid.toLocaleString('fr-FR')} €</span>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={11} className="p-8 text-center text-slate-500">
                              <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                              <p>Aucun contrat actif</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Legend */}
              <div className="flex flex-wrap gap-6 justify-center p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                    <Check size={14} />
                  </span>
                  <span className="text-sm text-slate-600">Payé</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">
                    ✓
                  </span>
                  <span className="text-sm text-slate-600">Validé (non payé)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold">
                    ⏳
                  </span>
                  <span className="text-sm text-slate-600">En attente</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600">
                    <Plus size={14} />
                  </span>
                  <span className="text-sm text-slate-600">Manquante (cliquer pour choisir)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600 text-xs font-bold">
                    N/A
                  </span>
                  <span className="text-sm text-slate-600">Marqué N/A (cliquer pour supprimer)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-400 text-xs">
                    N/A
                  </span>
                  <span className="text-sm text-slate-600">Avant date d'effet</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-300">-</span>
                  <span className="text-sm text-slate-600">Non applicable (unique)</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <Card className="bg-white shadow-lg border-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Date</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Apporteur</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Montant</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Mode</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Référence</th>
                      <th className="text-center p-4 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.length > 0 ? (
                      payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-slate-50">
                          <td className="p-4 text-slate-600">
                            {new Date(payment.payment_date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="p-4 font-medium text-slate-900">{payment.apporteur_name}</td>
                          <td className="p-4 font-medium text-green-600">{payment.total_amount?.toLocaleString('fr-FR')} €</td>
                          <td className="p-4 text-slate-600 capitalize">{payment.payment_method}</td>
                          <td className="p-4 text-slate-500">{payment.reference || '-'}</td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer ce paiement"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-slate-500">
                          <CreditCard size={48} className="mx-auto mb-4 text-slate-300" />
                          <p>Aucun paiement enregistré</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Period Action Menu (popup for + button) */}
      {periodActionMenu && (
        <>
          {/* Backdrop to close menu */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={closePeriodActionMenu}
          />
          {/* Menu */}
          <div 
            className="fixed z-50 bg-white rounded-lg shadow-xl border border-slate-200 py-2 min-w-[200px]"
            style={{ 
              left: Math.min(periodActionMenu.x, window.innerWidth - 220), 
              top: periodActionMenu.y 
            }}
          >
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-xs text-slate-500 font-medium">
                {MONTHS.find(m => m.value === String(periodActionMenu.month))?.label} {periodActionMenu.year}
              </p>
            </div>
            <button
              onClick={handleCreateCommissionFromMenu}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                <Plus size={16} />
              </span>
              <div>
                <p className="font-medium text-slate-900">Créer une commission</p>
                <p className="text-xs text-slate-500">Enregistrer le versement pour cette période</p>
              </div>
            </button>
            <button
              onClick={handleMarkPeriodNA}
              className="w-full px-4 py-3 text-left hover:bg-amber-50 flex items-center gap-3 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold">
                N/A
              </span>
              <div>
                <p className="font-medium text-slate-900">Marquer N/A</p>
                <p className="text-xs text-slate-500">Pas de commission pour cette période</p>
              </div>
            </button>
            <div className="border-t border-slate-100 mt-1 pt-1">
              <button
                onClick={closePeriodActionMenu}
                className="w-full px-4 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
